import {
  ORIG_KEYWORD_LINE,
  ORIG_PRODUCT,
  ORIG_PRODUCT_VARIANTS,
  ORIG_SUBTITLE_SINGLE,
  ORIG_SUBTITLE_DOUBLE,
  ORIG_SUBTITLE_TEXTS,
  ORIG_TRAILING,
  ORIG_CARD1_HREF_MP4,
  ORIG_CARD2_HREF_MP4,
  ORIG_IMAGE_URL,
  ORIG_CANONICAL_URL,
  DEFAULT_HOSTING_BASE,
  ORIG_TOKENS,
  IMAGE_HOSTS,
  VIDEO_HOST,
  PROTECTED_DOMAINS,
  STRUCTURAL_FINGERPRINTS,
} from './constants.js';
import {
  randBracketToken,
  randImageUrl,
  randVideoUrl,
  randUpperAlnum,
} from './random.js';
import { jsonStringEscape, htmlAttrEscape, htmlTextEscape } from './escaping.js';
import { stripGeo, sanitiseAllLlmJsonFields } from './geoSanitiser.js';
import { buildEvaluationHtml } from './evaluationArticle.js';
import { buildAllVideoSchemas, buildHydrationVideoSchemaStr } from './videoSchemaBuilder.js';
import { normaliseBase } from './canonicalPatcher.js';

const DEFAULT_SCRIPT_BASE = 'https://example.com/js/';

// ── Hydration VideoObject replacement ────────────────────────────────────────
const replaceHydrationVideoSchema = (html, escapedSchemaStr, log) => {
  const needle1 = '"type":"application\\/ld+json","children":"';
  const idx1 = html.indexOf(needle1);
  if (idx1 !== -1) {
    const valueStart = idx1 + needle1.length;
    const closingIdx = html.indexOf('"}]', valueStart);
    if (closingIdx !== -1) {
      log('[jsonld.hyd] Replaced hydration-payload VideoObject (pattern 1)');
      return html.slice(0, valueStart) + escapedSchemaStr + html.slice(closingIdx);
    }
    const closingIdx2 = html.indexOf('"}\n', valueStart);
    if (closingIdx2 !== -1) {
      log('[jsonld.hyd] Replaced hydration-payload VideoObject (pattern 1b)');
      return html.slice(0, valueStart) + escapedSchemaStr + html.slice(closingIdx2);
    }
  }
  const needle2 = '"type":"application/ld+json","children":"';
  const idx2 = html.indexOf(needle2);
  if (idx2 !== -1) {
    const valueStart = idx2 + needle2.length;
    const closingIdx = html.indexOf('"}]', valueStart);
    if (closingIdx !== -1) {
      log('[jsonld.hyd] Replaced hydration-payload VideoObject (pattern 2)');
      return html.slice(0, valueStart) + escapedSchemaStr + html.slice(closingIdx);
    }
  }
  log('[jsonld.hyd] WARNING: hydration VideoObject pattern not found — static HTML schemas still injected correctly');
  return html;
};

// ── Hydration JSON field replacement ─────────────────────────────────────────
const replaceHydrationJsonField = (html, label, searchFor, closingStr, newValue, log, asIs = false) => {
  // Titles are written as-is; other fields are geo-stripped.
  const cleanValue = asIs ? newValue : stripGeo(newValue);
  const esc = jsonStringEscape(cleanValue);
  const idx = html.indexOf(searchFor);
  if (idx === -1) {
    log(`[hyd.${label}] WARNING: marker not found, skipped`);
    return html;
  }
  const valueStart = idx + searchFor.length;
  const valueEnd = html.indexOf(closingStr, valueStart);
  if (valueEnd === -1) {
    log(`[hyd.${label}] WARNING: closing marker not found, skipped`);
    return html;
  }
  log(`[hyd.${label}] updated (${cleanValue.length} chars)`);
  return html.slice(0, valueStart) + esc + html.slice(valueEnd);
};

const replaceAllInHydration = (html, searchStr, replaceStr, label, log) => {
  const count = html.split(searchStr).length - 1;
  if (count > 0) {
    log(`[hyd.sweep.${label}] replaced ${count} occurrences`);
    return html.split(searchStr).join(replaceStr);
  }
  return html;
};

// ── Replace ALL .mp4 URLs from the template's video host ─────────────────────
// This handles href="...", background-image:url("..."), src="...", etc.
const replaceAllMp4Urls = (html, defaultVideoHost, rng, effectiveVideoBase, log) => {
  // Build a regex that matches the original template video host .mp4 URLs
  // in any context (href, url(), src, plain text in JSON payloads)
  const escapedHost = defaultVideoHost.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const mp4Rx = new RegExp(escapedHost + '\\/[A-Za-z0-9_-]+\\.mp4', 'g');
  let count = 0;
  const result = html.replace(mp4Rx, () => {
    count++;
    return randVideoUrl(defaultVideoHost, rng, effectiveVideoBase);
  });
  if (count > 0) {
    log(`[video.mp4.sweep] Replaced ${count} .mp4 URL(s) from template video host → ${effectiveVideoBase || defaultVideoHost}`);
  }
  return result;
};

// ── Main Export ───────────────────────────────────────────────────────────────
export const applyTransformations = (
  template,
  keyword,
  product,
  canonical,
  llmJson,
  rng,
  customImageBase,
  customVideoBase,
  canonicalBase,
  scriptBase = null,
  scriptName = null
) => {
  let out = template;
  const changes = [];
  const log = (msg) => changes.push(msg);

  sanitiseAllLlmJsonFields(llmJson);
  log('[geo.sanitise] Applied full geo-strip to ALL llmJson fields');
  log('[pre-sanitise] Sanitisation disabled per strict template rules');

  const effectiveBase = normaliseBase(canonicalBase);
  const isCustomBase = effectiveBase !== DEFAULT_HOSTING_BASE;
  const effectiveImageBase = customImageBase || null;
  const effectiveVideoBase = customVideoBase || null;

  // ── STEP A: Replace ALL .mp4 URLs FIRST (before canonical base sweep) ──────
  // This ensures video URLs always use the Custom Video URL Path and are never
  // accidentally rewritten by the canonical base replacement below.
  out = replaceAllMp4Urls(out, VIDEO_HOST, rng, effectiveVideoBase, log);

  // ── STEP B: Replace the default hosting base (html/canonical URLs only) ─────
  // We run this AFTER mp4 replacement so .mp4 URLs are already handled.
  if (isCustomBase) {
    const escapedDefault = DEFAULT_HOSTING_BASE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    // Only replace occurrences that are NOT followed by a .mp4 extension
    // (mp4s were already handled above)
    const s3Rx = new RegExp(escapedDefault + '(?![A-Za-z0-9_-]*\\.mp4)', 'g');
    const earlyCount = (out.match(s3Rx) || []).length;
    if (earlyCount > 0) {
      out = out.replace(s3Rx, effectiveBase);
      log(
        `[canonical.base] Replaced ${earlyCount} occurrence(s) of default hosting base ` +
          `"${DEFAULT_HOSTING_BASE}" → "${effectiveBase}"`
      );
    } else {
      log(
        `[canonical.base] No occurrences of default hosting base found in template ` +
          `(custom base: ${effectiveBase})`
      );
    }
  } else {
    log(`[canonical.base] Using default hosting base: ${DEFAULT_HOSTING_BASE}`);
  }

  const swap = (label, old, newStr, required = true) => {
    if (!out.includes(old)) {
      if (required)
        throw new Error(
          `Substitution failed: anchor not found for [${label}]: ${old.slice(0, 80)}`
        );
      log(`[${label}] WARNING: anchor not found, skipped`);
      return;
    }
    const count = out.split(old).length - 1;
    out = out.split(old).join(newStr);
    log(`[${label}] replaced x${count}: ${old.slice(0, 60)} -> ${newStr.slice(0, 60)}`);
  };

  const subtitle = stripGeo(llmJson.subtitle.trim());
  const trailing = stripGeo(llmJson.trailing.trim());

  let subDouble;
  if (subtitle.startsWith('(') && subtitle.endsWith(')')) {
    subDouble = '(' + subtitle + ')';
  } else {
    subDouble = '((' + subtitle.replace(/^\(+|\)+$/g, '') + '))';
  }

  const origFullSingle = `${ORIG_KEYWORD_LINE} ${ORIG_SUBTITLE_SINGLE} ${ORIG_TRAILING}`;
  const origFullDouble = `${ORIG_KEYWORD_LINE} ${ORIG_SUBTITLE_DOUBLE} ${ORIG_TRAILING}`;

  const now = new Date();
  const year = now.getFullYear();
  const isoDate = now.toISOString().split('.')[0];

  const host = rng.choice(IMAGE_HOSTS);

  if (effectiveImageBase) log(`[images.custom] Using custom thumbnail base: ${effectiveImageBase}`);
  else log(`[images.host] Using default image host: ${host}`);
  if (effectiveVideoBase) log(`[video.custom] Using custom video base: ${effectiveVideoBase}`);
  else log(`[video.host] Using default video host: ${VIDEO_HOST}`);

  // (B) Bracket tokens
  for (const tok of ORIG_TOKENS) {
    const newTok = randBracketToken(rng);
    swap(`token.${tok}`, tok, newTok, false);
  }

  // (C) Image URLs — replace primary image URL
  const imgCount = out.split(ORIG_IMAGE_URL).length - 1;
  if (imgCount > 0) {
    const imgPieces = out.split(ORIG_IMAGE_URL);
    const newPieces = [imgPieces[0]];
    for (let i = 1; i < imgPieces.length; i++) {
      newPieces.push(randImageUrl(host, rng, effectiveImageBase));
      newPieces.push(imgPieces[i]);
    }
    out = newPieces.join('');
    log(`[images] replaced ${imgCount} image-URL occurrences`);
  } else {
    log('[images] WARNING: original image URL not found — skipped');
  }

  // (C2) Replace ALL remaining neurofys.org image URLs
  const neurofysRx = /https:\/\/neurofys\.org\/images\/[A-Z0-9]+\/[0-9]+\.jpg/g;
  let neurofysCount = 0;
  out = out.replace(neurofysRx, () => {
    neurofysCount++;
    return randImageUrl(host, rng, effectiveImageBase);
  });
  if (neurofysCount > 0) {
    log(`[images.neurofys] replaced ${neurofysCount} additional neurofys.org image URLs`);
  }

  // (D) Named card video .mp4 URLs — these were already replaced in STEP A,
  // but we log them as handled so the change log is informative.
  // If for any reason they weren't caught (different host), swap them now.
  swap('card1.href.mp4', ORIG_CARD1_HREF_MP4, randVideoUrl(VIDEO_HOST, rng, effectiveVideoBase), false);
  swap('card2.href.mp4', ORIG_CARD2_HREF_MP4, randVideoUrl(VIDEO_HOST, rng, effectiveVideoBase), false);

  // (F) Canonical URL
  swap('canonical', ORIG_CANONICAL_URL, canonical, false);

  // The new template siteroot is lanetacampeche.com
  const ORIG_SITEROOT_PLAIN = 'https://lanetacampeche.com';
  out = replaceAllInHydration(
    out,
    `"og:url","content":"https://lanetacampeche.com`,
    `"og:url","content":"${canonical}`,
    'og_url_hyd',
    log
  );
  out = replaceAllInHydration(
    out,
    `"siteroot":"${ORIG_SITEROOT_PLAIN}"`,
    `"siteroot":"${canonical}"`,
    'siteroot',
    log
  );
  out = replaceAllInHydration(
    out,
    `"siteroot":"https:\\/\\/lanetacampeche.com"`,
    `"siteroot":"${canonical}"`,
    'siteroot_esc',
    log
  );

  // (G) Full keyword line replacements: this line is the page title, so it
  // gets the user's SEO title + code exactly.
  const newFullDouble = llmJson.seo_title || `${keyword} ${subDouble} ${trailing}`;
  const newFullSingle = llmJson.seo_title || `${keyword} ${subtitle} ${trailing}`;
  swap('full_double', origFullDouble, newFullDouble, false);
  swap('full_single', origFullSingle, newFullSingle, false);

  // (H) Meta tag content values
  const replaceMetaContent = (label, pattern, newValue) => {
    const cleanVal = stripGeo(newValue);
    const esc = htmlAttrEscape(cleanVal);
    const rx = new RegExp(pattern);
    const newOut = out.replace(rx, (m, p1, p2, p3) => p1 + esc + p3);
    if (newOut === out) {
      log(`[meta.${label}] WARNING: pattern not matched, skipped`);
      return;
    }
    out = newOut;
    log(`[meta.${label}] updated (${cleanVal.length} chars)`);
  };

  replaceMetaContent(
    'description',
    '(<meta name="description" content=")([^"]*)("/>)',
    llmJson.meta_description
  );
  replaceMetaContent(
    'keywords',
    '(<meta name="keywords" content=")([^"]*)("/>)',
    llmJson.meta_keywords
  );
  replaceMetaContent(
    'og:description',
    '(<meta property="og:description" content=")([^"]*)("/>)',
    llmJson.og_description_head
  );
  replaceMetaContent(
    'twitter:description',
    '(<meta name="twitter:description" content=")([^"]*)("/>)',
    llmJson.twitter_description_head
  );
  replaceMetaContent(
    'Copyright',
    '(<meta name="Copyright" content=")([^"]*)("/>)',
    `Copyright ${year} ${product} Instructions ${product} Official Website. All Rights Reserved.`
  );

  // (H2) Replace <title> tag content
  if (llmJson.seo_title && typeof llmJson.seo_title === 'string') {
    const seoTitle = llmJson.seo_title.trim();
    const titleTagRx = /(<title>)([^<]*)(<\/title>)/i;
    const newOutTitle = out.replace(
      titleTagRx,
      (m, p1, p2, p3) => p1 + htmlTextEscape(seoTitle) + p3
    );
    if (newOutTitle !== out) {
      out = newOutTitle;
      log(`[meta.seo_title] <title> tag updated: "${seoTitle}" (${seoTitle.length} chars)`);
    } else {
      log('[meta.seo_title] WARNING: <title> tag not found, skipped');
    }
  } else {
    log('[meta.seo_title] WARNING: seo_title not provided by LLM, skipped');
  }

  // (H3) Replace og:title meta tag
  const replaceMetaOgTitle = (newValue) => {
    const cleanVal = newValue;
    const esc = htmlAttrEscape(cleanVal);
    const rx = /(<meta[^>]*property="og:title"[^>]*content=")([^"]*)(">)/i;
    const newOut = out.replace(rx, (m, p1, p2, p3) => p1 + esc + p3);
    if (newOut !== out) {
      out = newOut;
      log(`[meta.og:title] updated (${cleanVal.length} chars)`);
    } else {
      log('[meta.og:title] WARNING: og:title meta not matched in <head>, skipped');
    }
  };
  if (llmJson.hyd_og_title) replaceMetaOgTitle(llmJson.hyd_og_title);

  // (H4) Replace twitter:title meta tag
  const replaceMetaTwitterTitle = (newValue) => {
    const cleanVal = newValue;
    const esc = htmlAttrEscape(cleanVal);
    const rx = /(<meta[^>]*name="twitter:title"[^>]*content=")([^"]*)(">)/i;
    const newOut = out.replace(rx, (m, p1, p2, p3) => p1 + esc + p3);
    if (newOut !== out) {
      out = newOut;
      log(`[meta.twitter:title] updated (${cleanVal.length} chars)`);
    } else {
      log('[meta.twitter:title] WARNING: twitter:title not matched, skipped');
    }
  };
  if (llmJson.hyd_twitter_title) replaceMetaTwitterTitle(llmJson.hyd_twitter_title);

  // (H5) Replace <meta name="title"> tag
  const replaceMetaNameTitle = (newValue) => {
    const cleanVal = newValue;
    const esc = htmlAttrEscape(cleanVal);
    const rx = /(<meta[^>]*name="title"[^>]*content=")([^"]*)(">)/i;
    const newOut = out.replace(rx, (m, p1, p2, p3) => p1 + esc + p3);
    if (newOut !== out) {
      out = newOut;
      log(`[meta.name_title] updated (${cleanVal.length} chars)`);
    } else {
      log('[meta.name_title] WARNING: <meta name="title"> not matched, skipped');
    }
  };
  if (llmJson.hyd_meta_title) replaceMetaNameTitle(llmJson.hyd_meta_title);

  // (I) Breadcrumb blurb
  // Written as-is: prepending the long keyword phrase made sentences unnatural.
  const newBreadcrumb = stripGeo(llmJson.breadcrumb_blurb);
  const breadcrumbRx = /(<span class="cursordefault">)([^<]*?)(<\/span>)/;
  const newOutBC = out.replace(
    breadcrumbRx,
    (m, p1, p2, p3) => p1 + htmlTextEscape(newBreadcrumb) + p3
  );
  if (newOutBC !== out) {
    out = newOutBC;
    log('[body.breadcrumb_blurb] updated');
  } else {
    log('[body.breadcrumb_blurb] WARNING: no match, skipped');
  }

  // (J) Date line
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December',
  ];
  const niceDate = `${months[now.getMonth()]} ${String(now.getDate()).padStart(2, '0')}, ${year}`;
  const dateProduct = stripGeo(llmJson.video_title_date_product);
  const newDateLine = `<p class="videodatecompany source-color">${niceDate}<!-- --> |<!-- --> <!-- -->${htmlTextEscape(dateProduct)}</p>`;
  const dateRx =
    /<p class="videodatecompany source-color">[^<]*<!-- --> \|<!-- --> <!-- -->[^<]*<\/p>/;
  const newOutDate = out.replace(dateRx, newDateLine);
  if (newOutDate !== out) {
    out = newOutDate;
    log(`[body.date_line] set to ${niceDate} | ${dateProduct}`);
  } else {
    log('[body.date_line] WARNING: no match, skipped');
  }

  // (K) Main video description (intro) + evaluation article, same as the
  // Story template.
  const newMainDesc = stripGeo(llmJson.main_video_description);
  const firstIntro = stripGeo(llmJson.article?.intro?.[0] || llmJson.main_video_description);
  const evaluationHtml = buildEvaluationHtml(llmJson.article, product, { ...(llmJson.research || {}), rng });
  // Remaining intro paragraphs (the first fills the template's <p>).
  const moreIntro = (llmJson.article?.intro || []).slice(1).map((t) => `<p>${htmlTextEscape(stripGeo(t))}</p>`).join('');
  const mainDescRx =
    /(<input type="hidden" name="video_id" value="25212"\/?>)<p>([\s\S]*?)<\/p>/;
  const newOutMD = out.replace(mainDescRx, (m, p1) => {
    return p1 + `<p>${htmlTextEscape(firstIntro)}</p>` + moreIntro + evaluationHtml;
  });
  if (newOutMD !== out) {
    out = newOutMD;
    log(`[body.main_video_description] updated text inside <p> tag`);
  } else {
    const mainDescRx2 = /(<input[^>]*name="video_id"[^>]*>)<p>([\s\S]*?)<\/p>/;
    const newOutMD2 = out.replace(mainDescRx2, (m, p1) => {
      return p1 + `<p>${htmlTextEscape(firstIntro)}</p>` + moreIntro + evaluationHtml;
    });
    if (newOutMD2 !== out) {
      out = newOutMD2;
      log(`[body.main_video_description] updated (fallback pattern)`);
    } else {
      log('[body.main_video_description] WARNING: no match, skipped');
    }
  }

  // (L) Six morevideotitle — card titles
  let titleIdx = 0;
  const titleRx = /<p class="morevideotitle responsemargintop"> <!-- -->[^<]*<\/p>/g;
  const newOutTitles = out.replace(titleRx, (m) => {
    if (titleIdx >= llmJson.card_titles.length) return m;
    const txt = stripGeo(llmJson.card_titles[titleIdx++]);
    return `<p class="morevideotitle responsemargintop"> <!-- -->${htmlTextEscape(txt)}</p>`;
  });
  if (titleIdx === 6) {
    out = newOutTitles;
    log('[body.morevideotitle] updated 6 card titles');
  } else {
    log(`[body.morevideotitle] WARNING: only matched ${titleIdx}/6`);
  }

  // Six morevideocontent paragraphs
  let contentIdx = 0;
  const contentRx = /<p class="morevideocontent responsedisplaynone">[^<]*<\/p>/g;
  const newOutContents = out.replace(contentRx, (m) => {
    if (contentIdx >= llmJson.card_contents.length) return m;
    const txt = stripGeo(llmJson.card_contents[contentIdx++]);
    return `<p class="morevideocontent responsedisplaynone">${htmlTextEscape(txt)}</p>`;
  });
  if (contentIdx === 6) {
    out = newOutContents;
    log('[body.morevideocontent] updated 6 card contents');
  } else {
    log(`[body.morevideocontent] WARNING: only matched ${contentIdx}/6`);
  }

  // (M) Inject VideoObject schemas
  const jsonLdRx = /<script type="application\/ld\+json">\{[^<]*\}<\/script>/;
  out = out.replace(jsonLdRx, '<!-- json-ld-placeholder -->');

  // Video chapters (hasPart) only come from the owner's real timestamps.
  // Generated chapter times would describe moments nobody has seen.
  const moments = llmJson.research?.evidence?.videoMoments || [];
  const randomDuration = 180 + Math.floor(rng._next() * 300);
  const primaryDuration = moments.length
    ? Math.max(randomDuration, moments[moments.length - 1].seconds + 30)
    : randomDuration;
  const primaryHasPart = moments.map((m, i) => {
    const end = i === moments.length - 1 ? primaryDuration : moments[i + 1].seconds - 1;
    return { name: stripGeo(m.description), start: m.seconds, end, url: `${canonical}#t=${m.seconds}` };
  });

  const { allBlocks, totalCount } = buildAllVideoSchemas({
    keyword,
    product,
    canonical,
    isoDate,
    host,
    cardContents: llmJson.card_contents,
    rng,
    customImageBase: effectiveImageBase,
    customVideoBase: effectiveVideoBase,
    primarySubtitle: subtitle,
    primaryDuration,
    primaryHasPart,
    secondaryVideoSubtitles: llmJson.secondary_video_subtitles || [],
    secondaryChapterSets: llmJson.secondary_chapter_sets || [],
  });

  out = out.replace('<!-- json-ld-placeholder -->', allBlocks.join('\n'));
  log(`[jsonld] Injected ${totalCount} fully unique VideoObject schemas`);

  // (M2) Replace VideoObject inside Next.js hydration payload
  const hydSchemaStr = buildHydrationVideoSchemaStr({
    keyword,
    product,
    subtitle,
    canonical,
    isoDate,
    host,
    primaryHasPart,
    primaryDuration,
    rng,
    customImageBase: effectiveImageBase,
    customVideoBase: effectiveVideoBase,
  });
  out = replaceHydrationVideoSchema(out, hydSchemaStr, log);

  // (N) Hydration meta fields
  const hydMetaFields = [
    { label: 'og:title', propKind: 'property', propName: 'og:title', value: llmJson.hyd_og_title, asIs: true },
    { label: 'og:description', propKind: 'property', propName: 'og:description', value: llmJson.hyd_og_description },
    { label: 'twitter:title', propKind: 'name', propName: 'twitter:title', value: llmJson.hyd_twitter_title, asIs: true },
    { label: 'twitter:description', propKind: 'name', propName: 'twitter:description', value: llmJson.hyd_twitter_description },
  ];
  for (const f of hydMetaFields) {
    const marker = `"${f.propKind}":"${f.propName}","content":"`;
    out = replaceHydrationJsonField(out, f.label, marker, '"', f.value, log, f.asIs);
  }

  // (O) Hydration title meta and <title> element
  out = replaceHydrationJsonField(out, 'meta_title', '"name":"title","content":"', '"', llmJson.hyd_meta_title, log, true);
  out = replaceHydrationJsonField(out, 'title_element', '"children":"', '"', llmJson.hyd_title_element, log, true);
  out = replaceHydrationJsonField(out, 'og:url', '"property":"og:url","content":"', '"', canonical, log);

  // (P) Final sweeps
  const kwBefore = out.split(ORIG_KEYWORD_LINE).length - 1;
  if (kwBefore > 0) {
    out = out.split(ORIG_KEYWORD_LINE).join(keyword);
    log(`[sweep.keyword] swept ${kwBefore} residual`);
  }

  const subDBefore = out.split(ORIG_SUBTITLE_DOUBLE).length - 1;
  if (subDBefore > 0) {
    out = out.split(ORIG_SUBTITLE_DOUBLE).join(subDouble);
    log(`[sweep.subtitle_double] swept ${subDBefore} residual`);
  }

  const subSBefore = out.split(ORIG_SUBTITLE_SINGLE).length - 1;
  if (subSBefore > 0) {
    out = out.split(ORIG_SUBTITLE_SINGLE).join(subtitle);
    log(`[sweep.subtitle_single] swept ${subSBefore} residual`);
  }

  const trBefore = out.split(ORIG_TRAILING).length - 1;
  if (trBefore > 0) {
    out = out.split(ORIG_TRAILING).join(trailing);
    log(`[sweep.trailing] swept ${trBefore} residual`);
  }

  let productSweepCount = 0;
  for (const variant of ORIG_PRODUCT_VARIANTS) {
    if (variant === product) continue;
    const before = out.split(variant).length - 1;
    if (before > 0) {
      out = out.split(variant).join(product);
      productSweepCount += before;
      log(`[sweep.product_variant] swept ${before} of '${variant}'`);
    }
  }
  if (productSweepCount === 0) log('[sweep.product_variant] No residual product name variants found');

  let subtitleSweepCount = 0;
  for (const st of ORIG_SUBTITLE_TEXTS) {
    const before = out.split(st).length - 1;
    if (before > 0) {
      out = out.split(st).join(subtitle);
      subtitleSweepCount += before;
    }
  }
  if (subtitleSweepCount > 0)
    log(`[sweep.subtitle_texts] swept ${subtitleSweepCount} old subtitle occurrences`);

  const GEO_TRAILING_MARKERS = [
    'Where to buy, Price , Beneftis',
    'UK,CA,AUS,Ingredients,Official Website',
    'UK,CA,AUS',
    'Ingredients,Official Website',
  ];
  for (const marker of GEO_TRAILING_MARKERS) {
    const before = out.split(marker).length - 1;
    if (before > 0) {
      out = out.split(marker).join(trailing);
      log(`[sweep.geo_marker] swept ${before} of "${marker}"`);
    }
  }

  // (Q) Randomize Next.js CSS/JS filenames
  const nextAssetRx =
    /((?:\\?\/)_next(?:\\?\/)static(?:\\?\/)(?:[A-Za-z0-9_/%[\]-]+(?:\\?\/))*?(?:page-)?)([A-Za-z0-9_-]+)(\.(?:css|js))/g;
  const hashMap = new Map();
  let assetCount = 0;
  out = out.replace(nextAssetRx, (match, prefix, filename, suffix) => {
    if (!hashMap.has(filename)) {
      hashMap.set(filename, randUpperAlnum(13, rng));
    }
    assetCount++;
    return `${prefix}${hashMap.get(filename)}${suffix}`;
  });
  if (assetCount > 0) {
    log(
      `[assets.randomize] Randomized ${assetCount} Next.js CSS/JS asset occurrences (${hashMap.size} unique files)`
    );
  }

  // (S) Inject redirect script tag just BEFORE </head>
  const effectiveScriptBase =
    scriptBase && scriptBase.trim()
      ? scriptBase.trim().endsWith('/')
        ? scriptBase.trim()
        : scriptBase.trim() + '/'
      : DEFAULT_SCRIPT_BASE;
  // The redirect script is named after the JS Redirect File Name, which can
  // differ from the brand name shown on the page.
  const productSlug = (scriptName || product || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const scriptSrc = `${effectiveScriptBase}${productSlug}.js`;
  const scriptTag = `<script src="${scriptSrc}" id="query" value="query"></script>`;
  const headCloseIdx = out.indexOf('</head>');
  if (headCloseIdx !== -1) {
    out = out.slice(0, headCloseIdx) + scriptTag + '\n' + out.slice(headCloseIdx);
    log(`[script.inject] Injected redirect script before </head>: ${scriptSrc}`);
  } else {
    log('[script.inject] WARNING: </head> not found — script tag not injected');
  }

  log('[post-sanitise] Sanitisation disabled per strict template rules');
  return { html: out, changes };
};

export const verifyProtectedDomains = (original, generated) => {
  const lines = [];
  let ok = true;
  for (const dom of PROTECTED_DOMAINS) {
    const before = original.split(dom).length - 1;
    const after = generated.split(dom).length - 1;
    if (after < before) {
      ok = false;
      lines.push({ status: 'FAIL', label: dom, before, after });
    } else {
      lines.push({ status: 'OK', label: dom, before, after });
    }
  }
  for (const frag of STRUCTURAL_FINGERPRINTS) {
    const before = original.split(frag).length - 1;
    const after = generated.split(frag).length - 1;
    if (after < before) {
      ok = false;
      lines.push({ status: 'FAIL', label: frag, before, after });
    } else {
      lines.push({ status: 'OK', label: frag, before, after });
    }
  }
  return { ok, report: lines };
};